import React, {useState} from 'react';
import {Upload, Download, X, CheckCircle, FileText, TrendingUp, TrendingDown, Users} from 'lucide-react';
import Notification from "../components/Common/Notification";

// Same look as the catalogue import popup (src/components/CatalogPage/CatalogPage.js)

const ModalCard = ({title, onClose, children, footer}) => (
    <div className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-lg shadow-lg w-full sm:max-w-[500px] sm:mx-4 max-h-[90vh] sm:max-h-[85vh] overflow-y-auto">
        <div className="relative p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-medium text-center text-gray-800 dark:text-white">{title}</h2>
            <button
                type="button"
                onClick={onClose}
                aria-label="Fermer"
                className="absolute right-4 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
                <X className="w-4 h-4"/>
            </button>
        </div>
        {children}
        {footer}
    </div>
);

// Two-cell "Annuler | Action" footer
const FooterButtons = ({onCancel, onConfirm, confirmLabel, disabled}) => (
    <div className="grid grid-cols-2 border-t border-gray-200 dark:border-gray-700">
        <button className="py-4 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium" onClick={onCancel}>
            Annuler
        </button>
        <button
            className="py-4 text-primary font-medium border-l border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={onConfirm}
            disabled={disabled}
        >
            {confirmLabel}
        </button>
    </div>
);

// Bordered choice row; `selected` shows the teal check bubble of the catalogue radios
const ChoiceRow = ({icon: Icon, title, desc, onClick, selected}) => (
    <button
        type="button"
        onClick={onClick}
        className={`w-full block border rounded-lg cursor-pointer transition overflow-hidden text-left ${
            selected ? 'border-primary' : 'border-gray-200 dark:border-gray-600 hover:border-primary'
        }`}
    >
        <div className="flex items-start p-4">
            <div className="mr-3 mt-1 flex-shrink-0">
                {Icon ? (
                    <Icon className="w-5 h-5 text-primary"/>
                ) : (
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center ${selected ? 'bg-primary' : 'border border-gray-300 dark:border-gray-500'}`}>
                        {selected && (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-white" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd"
                                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                      clipRule="evenodd"/>
                            </svg>
                        )}
                    </div>
                )}
            </div>
            <div>
                <p className="font-medium text-gray-800 dark:text-white">{title}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{desc}</p>
            </div>
        </div>
    </button>
);

const StatRow = ({icon: Icon, label, value, tone}) => (
    <div className="flex items-center justify-between py-3">
        <span className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-300">
            <Icon className={`w-5 h-5 ${tone}`}/>
            {label}
        </span>
        <span className={`text-xl font-bold ${tone}`}>{value}</span>
    </div>
);

const ClientImportExportModal = ({
                                     isVisible,
                                     onClose,
                                     onImport,
                                     onExport,
                                     isLoading = false,
                                 }) => {
    const [modalType, setModalType] = useState(null); // 'import' or 'export'
    const [step, setStep] = useState(1);
    const [importOption, setImportOption] = useState('modify');
    const [selectedFile, setSelectedFile] = useState(null);
    const [notification, setNotification] = useState({
        show: false,
        type: 'success',
        message: ''
    });
    const [importStats, setImportStats] = useState(null);

    const busy = isLoading || (notification.show && notification.type === 'loading');

    const showNotification = (type, message) => {
        setNotification({show: true, type, message});
        if (type !== 'loading') {
            setTimeout(() => setNotification(prev => ({...prev, show: false})), 5000);
        }
    };

    const hideNotification = () => {
        setNotification(prev => ({...prev, show: false}));
    };

    const resetFileInput = () => {
        const fileInput = document.getElementById('client-file-input');
        if (fileInput) fileInput.value = '';
    };

    const handleClose = () => {
        setModalType(null);
        setStep(1);
        setImportOption('modify');
        setSelectedFile(null);
        setImportStats(null);
        hideNotification();
        onClose();
    };

    const handleImportFile = async () => {
        if (!selectedFile) {
            showNotification("error", "Aucun fichier sélectionné");
            return;
        }

        try {
            showNotification("loading", "Importation en cours...");
            const result = await onImport(selectedFile, importOption);

            // Capturer les statistiques
            if (result && result.stats) {
                setImportStats(result.stats);
                setStep(3); // Passer à l'étape d'affichage des stats
                hideNotification();
            } else {
                showNotification("success", "Clients importés avec succès");
                setTimeout(handleClose, 2000);
            }

            setSelectedFile(null);
            resetFileInput();
        } catch (error) {
            showNotification("error", error.message || "Erreur lors de l'importation");
        }
    };

    const handleExportClients = async () => {
        try {
            showNotification("loading", "Export en cours...");
            await onExport();
            showNotification("success", "Export terminé avec succès");
            setTimeout(handleClose, 2000);
        } catch (error) {
            showNotification("error", error.message || "Erreur lors de l'export");
        }
    };

    if (!isVisible) return null;

    let content;
    if (!modalType) {
        // Initial choice: Import or Export
        content = (
            <ModalCard
                title="Gestion des clients"
                onClose={handleClose}
                footer={
                    <div className="border-t border-gray-200 dark:border-gray-700">
                        <button className="w-full py-4 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium" onClick={handleClose}>
                            Annuler
                        </button>
                    </div>
                }
            >
                <div className="px-6 py-4 space-y-3">
                    <ChoiceRow icon={Upload} title="Importer des clients"
                               desc="Importer une liste de clients depuis un fichier Excel ou CSV"
                               onClick={() => setModalType('import')}/>
                    <ChoiceRow icon={Download} title="Exporter les clients"
                               desc="Télécharger la liste complète de vos clients"
                               onClick={() => setModalType('export')}/>
                </div>
            </ModalCard>
        );
    } else if (modalType === 'export') {
        content = (
            <ModalCard
                title="Exporter les clients"
                onClose={handleClose}
                footer={<FooterButtons onCancel={handleClose} onConfirm={handleExportClients} disabled={busy}
                                       confirmLabel={busy ? 'Export...' : 'Exporter'}/>}
            >
                <div className="px-6 py-8 text-center">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Download className="w-8 h-8 text-primary"/>
                    </div>
                    <p className="text-lg font-medium mb-2 text-gray-800 dark:text-white">Prêt à exporter</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        Cliquez sur « Exporter » pour télécharger la liste complète de vos clients au format Excel.
                    </p>
                </div>
            </ModalCard>
        );
    } else if (step === 1) {
        content = (
            <ModalCard
                title="Comment souhaitez-vous importer ?"
                onClose={handleClose}
                footer={<FooterButtons onCancel={handleClose} onConfirm={() => setStep(2)} confirmLabel="Suivant"/>}
            >
                <div className="px-6 py-4 space-y-3">
                    <ChoiceRow
                        title="Modifier la liste existante"
                        desc="Cette action permet de modifier les clients existants et d'en ajouter de nouveaux."
                        selected={importOption === 'modify'}
                        onClick={() => setImportOption('modify')}
                    />
                    <ChoiceRow
                        title="Écraser la liste existante"
                        desc="Cette action permet d'écraser la liste existante, ce qui aura pour effet de supprimer tous les clients et de les remplacer."
                        selected={importOption === 'overwrite'}
                        onClick={() => setImportOption('overwrite')}
                    />
                </div>
            </ModalCard>
        );
    } else if (step === 3) {
        content = (
            <ModalCard
                title="Import terminé"
                onClose={handleClose}
                footer={
                    <div className="border-t border-gray-200 dark:border-gray-700">
                        <button className="w-full py-4 text-primary font-medium hover:bg-gray-50 dark:hover:bg-gray-700" onClick={handleClose}>
                            Fermer
                        </button>
                    </div>
                }
            >
                <div className="px-6 py-6">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircle className="w-8 h-8 text-primary"/>
                    </div>
                    <p className="text-center text-lg font-medium mb-4 text-gray-800 dark:text-white">Importation réussie</p>

                    {importStats && (
                        <div className="divide-y divide-gray-200 dark:divide-gray-700 border-y border-gray-200 dark:border-gray-700">
                            <StatRow icon={TrendingUp} label="Clients ajoutés" value={importStats.added || 0}
                                     tone="text-green-600 dark:text-green-400"/>
                            <StatRow icon={Users} label="Clients mis à jour" value={importStats.updated || 0}
                                     tone="text-blue-600 dark:text-blue-400"/>
                            {importStats.skipped > 0 && (
                                <StatRow icon={TrendingDown} label="Lignes ignorées" value={importStats.skipped}
                                         tone="text-amber-600 dark:text-amber-400"/>
                            )}
                            <div className="flex justify-between items-center py-3">
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Total traité</span>
                                <span className="text-xl font-bold text-gray-900 dark:text-white">
                                    {(importStats.added || 0) + (importStats.updated || 0) + (importStats.skipped || 0)}
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            </ModalCard>
        );
    } else {
        // Import Step 2: File upload
        content = (
            <ModalCard
                title="Importer mon fichier clients"
                onClose={handleClose}
                footer={<FooterButtons onCancel={handleClose} onConfirm={handleImportFile} disabled={busy || !selectedFile}
                                       confirmLabel={busy ? 'Import...' : 'Importer'}/>}
            >
                <div className="px-6 py-4">
                    <button onClick={() => onExport && onExport(true)} className="btn-outline w-full">
                        Télécharger le modèle de fichier clients
                    </button>

                    <div
                        className="mt-6 p-4 border-dashed border-2 border-gray-300 dark:border-gray-600 rounded-md text-center hover:border-primary cursor-pointer"
                        onDragOver={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            e.currentTarget.classList.add('border-primary');
                        }}
                        onDragLeave={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            e.currentTarget.classList.remove('border-primary');
                        }}
                        onDrop={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            e.currentTarget.classList.remove('border-primary');
                            const files = e.dataTransfer.files;
                            if (files && files.length > 0) setSelectedFile(files[0]);
                        }}
                        onClick={() => {
                            if (!selectedFile) document.getElementById('client-file-input')?.click();
                        }}
                    >
                        <input
                            type="file"
                            id="client-file-input"
                            className="hidden"
                            accept=".xls,.xlsx,.csv"
                            onChange={(e) => {
                                if (e.target.files?.length > 0) setSelectedFile(e.target.files[0]);
                            }}
                        />

                        {!selectedFile ? (
                            <>
                                <Upload className="w-12 h-12 mx-auto text-gray-500 dark:text-gray-400 mb-1"/>
                                <p className="text-gray-700 dark:text-gray-300 font-semibold">Glisser mon fichier clients ici</p>
                                <p className="text-sm text-gray-500 dark:text-gray-400">Vous pouvez également le sélectionner directement</p>
                                <p className="text-xs text-gray-400 mt-2">Formats acceptés : Excel (.xlsx, .xls) ou CSV (.csv)</p>
                            </>
                        ) : (
                            <div className="flex flex-col items-center">
                                <div className="flex items-center justify-center w-12 h-12 mx-auto mb-2 bg-primary/10 rounded-full">
                                    <FileText className="w-6 h-6 text-primary"/>
                                </div>
                                <p className="text-gray-700 dark:text-gray-300 font-semibold">{selectedFile.name}</p>
                                <button
                                    className="mt-2 text-sm text-red-500 hover:text-red-700"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedFile(null);
                                        resetFileInput();
                                    }}
                                >
                                    Supprimer
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </ModalCard>
        );
    }

    return (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black bg-opacity-50">
            {content}

            {/* Notification */}
            <Notification
                show={notification.show}
                type={notification.type}
                message={notification.message}
                onClose={hideNotification}
            />
        </div>
    );
};

export default ClientImportExportModal;
